import React, { useEffect, useState, useRef } from "react";
import { useMediaQuery } from 'react-responsive';

// ICONS
import iconMap from "utils/iconMap";

const Toast = ({ title, message, action, actionText, type, onClose, index = 0, total = 1 }) => {
    // Initialiser directement avec la valeur actuelle pour éviter le flash
    const [isDarkMode, setIsDarkMode] = useState(() => {
        if (typeof window !== 'undefined') {
            return document.documentElement.classList.contains('dark');
        }
        return false;
    });
    const [isExiting, setIsExiting] = useState(false);
    const isMobile = useMediaQuery({ maxWidth: 640 });
    const [startY, setStartY] = useState(null);
    const toastRef = useRef(null);

    useEffect(() => {
        const toastElement = toastRef.current;
    
        if (toastElement) {
            const handleWheel = (event) => {
                event.preventDefault();
                event.stopPropagation();
            };
    
            toastElement.addEventListener('wheel', handleWheel, { passive: false });
    
            return () => {
                toastElement.removeEventListener('wheel', handleWheel);
            };
        }
    }, []);

    useEffect(() => {
        const htmlElement = document.documentElement;
        
        // Vérifier immédiatement pour éviter tout délai
        const checkDarkMode = () => {
            setIsDarkMode(htmlElement.classList.contains('dark'));
        };
        
        // Vérification initiale immédiate
        checkDarkMode();
        
        const observer = new MutationObserver(checkDarkMode);
        observer.observe(htmlElement, { attributes: true, attributeFilter: ['class'] });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            handleClose();
        }, 5000);
        
        return () => clearTimeout(timer);
    }, []);

    const handleClose = () => {
        setIsExiting(true);
        setTimeout(() => {
            setTimeout(onClose, 100);
        }, 300);
    };

    const handleTouchStart = (event) => {
        if (isMobile) {
            setStartY(event.touches[0].clientY);
        }
    };

    const handleTouchMove = (event) => {
        if (isMobile && startY !== null) {
            const currentY = event.touches[0].clientY;
            const diff = currentY - startY;
            
            if (diff > 0 && toastRef.current) {
                const opacity = Math.max(0, 1 - (diff / 200));
                toastRef.current.style.opacity = opacity;
                toastRef.current.style.transform = `translateY(${Math.min(diff, 200)}px)`;
            }
        }
    };

    const handleTouchEnd = (event) => {
        if (isMobile && startY !== null) {
            const endY = event.changedTouches[0].clientY;
            const diff = endY - startY;
            
            if (diff > 80) {
                handleClose();
            } else {
                // Reset position
                if (toastRef.current) {
                    toastRef.current.style.opacity = '1';
                    toastRef.current.style.transform = 'translateY(0)';
                }
            }
            setStartY(null);
        }
    };

    const CrossIcon = iconMap['crossSmall'];

    const getIcon = (type) => {
        if (isDarkMode) {
            switch (type) {
                case 'success':
                    return iconMap['toastDarkSuccess'];
                case 'error':
                    return iconMap['toastDarkError'];
                case 'warning':
                    return iconMap['toastDarkWarning'];
                case 'info':
                    return iconMap['toastDarkInfo'];
                default:
                    return iconMap['toastDarkInfo'];
            }
        } else {
            switch (type) {
                case 'success':
                    return iconMap['toastLightSuccess'];
                case 'error':
                    return iconMap['toastLightError'];
                case 'warning':
                    return iconMap['toastLightWarning'];
                case 'info':
                    return iconMap['toastLightInfo'];
                default:
                    return iconMap['toastLightInfo'];
            }
        }
    };

    const getStyles = (type) => {
        if (isDarkMode) {
            switch (type) {
                case 'success':
                    return {
                        background: 'bg-gradient-to-br from-[#1a3d2e] to-[#0f281f]',
                        borderColor: '#43D590',
                        iconBg: 'bg-[#43D590]/20',
                        iconBgHover: 'hover:bg-[#43D590]/30',
                        text: 'text-[#a8f5d0]',
                        textSecondary: 'text-[#7dd3a0]',
                        shadow: 'shadow-[0_8px_32px_rgba(67,213,144,0.15)]',
                        progressColor: '#43D590',
                        buttonBg: 'bg-[#43D590]',
                        buttonText: 'text-[#0f281f]',
                        buttonHover: 'hover:bg-[#32BB71]',
                    };
                case 'error':
                    return {
                        background: 'bg-gradient-to-br from-[#3d1f1a] to-[#28120f]',
                        borderColor: '#F0863A',
                        iconBg: 'bg-[#F0863A]/20',
                        iconBgHover: 'hover:bg-[#F0863A]/30',
                        text: 'text-[#ffb896]',
                        textSecondary: 'text-[#f5a372]',
                        shadow: 'shadow-[0_8px_32px_rgba(240,134,58,0.15)]',
                        progressColor: '#F0863A',
                        buttonBg: 'bg-[#F0863A]',
                        buttonText: 'text-[#28120f]',
                        buttonHover: 'hover:bg-[#F6743E]',
                    };
                case 'warning':
                    return {
                        background: 'bg-gradient-to-br from-[#3d2f1a] to-[#281f0f]',
                        borderColor: '#FFDF8D',
                        iconBg: 'bg-[#FFDF8D]/20',
                        iconBgHover: 'hover:bg-[#FFDF8D]/30',
                        text: 'text-[#ffe5a8]',
                        textSecondary: 'text-[#ffd966]',
                        shadow: 'shadow-[0_8px_32px_rgba(255,223,141,0.15)]',
                        progressColor: '#FFDF8D',
                        buttonBg: 'bg-[#FFDF8D]',
                        buttonText: 'text-[#281f0f]',
                        buttonHover: 'hover:bg-[#F8B806]',
                    };
                case 'info':
                    return {
                        background: 'bg-gradient-to-br from-[#1a2d3d] to-[#0f1f2a]',
                        borderColor: '#7BCFED',
                        iconBg: 'bg-[#7BCFED]/20',
                        iconBgHover: 'hover:bg-[#7BCFED]/30',
                        text: 'text-[#b8e5f6]',
                        textSecondary: 'text-[#9dd8f0]',
                        shadow: 'shadow-[0_8px_32px_rgba(123,207,237,0.15)]',
                        progressColor: '#7BCFED',
                        buttonBg: 'bg-[#7BCFED]',
                        buttonText: 'text-[#0f1f2a]',
                        buttonHover: 'hover:bg-[#329ABB]',
                    };
                default:
                    return {
                        background: 'bg-gradient-to-br from-[#1a2d3d] to-[#0f1f2a]',
                        borderColor: '#7BCFED',
                        iconBg: 'bg-[#7BCFED]/20',
                        iconBgHover: 'hover:bg-[#7BCFED]/30',
                        text: 'text-[#b8e5f6]',
                        textSecondary: 'text-[#9dd8f0]',
                        shadow: 'shadow-[0_8px_32px_rgba(123,207,237,0.15)]',
                        progressColor: '#7BCFED',
                        buttonBg: 'bg-[#7BCFED]',
                        buttonText: 'text-[#0f1f2a]',
                        buttonHover: 'hover:bg-[#329ABB]',
                    };
            }
        } else {
            switch (type) {
                case 'success':
                    return {
                        background: 'bg-gradient-to-br from-[#F0FDF4] to-[#DCFCE7]',
                        backgroundOverlay: 'bg-gris-20/50',
                        borderColor: '#48C1B5',
                        iconBg: 'bg-[#48C1B5]/15',
                        iconBgHover: 'hover:bg-[#48C1B5]/40',
                        text: 'text-[#065f46]',
                        textSecondary: 'text-[#047857]',
                        shadow: 'shadow-[0_8px_32px_rgba(72,193,181,0.25)]',
                        progressColor: '#48C1B5',
                        buttonBg: 'bg-[#047857]',
                        buttonText: 'text-blanc',
                        buttonHover: 'hover:bg-[#065f46]',
                        closeHover: 'hover:bg-[#065f46]/20',
                    };
                case 'error':
                    return {
                        background: 'bg-gradient-to-br from-[#FEF2F2] to-[#FEE2E2]',
                        backgroundOverlay: 'bg-gris-20/50',
                        borderColor: '#F4B0A1',
                        iconBg: 'bg-[#F4B0A1]/15',
                        iconBgHover: 'hover:bg-[#F4B0A1]/40',
                        text: 'text-[#991b1b]',
                        textSecondary: 'text-[#b91c1c]',
                        shadow: 'shadow-[0_8px_32px_rgba(244,176,161,0.25)]',
                        progressColor: '#F4B0A1',
                        buttonBg: 'bg-[#b91c1c]',
                        buttonText: 'text-blanc',
                        buttonHover: 'hover:bg-[#991b1b]',
                        closeHover: 'hover:bg-[#991b1b]/20',
                    };
                case 'warning':
                    return {
                        background: 'bg-gradient-to-br from-[#FFFBEB] to-[#FEF3C7]',
                        backgroundOverlay: 'bg-gris-20/50',
                        borderColor: '#F7D9A4',
                        iconBg: 'bg-[#F7D9A4]/15',
                        iconBgHover: 'hover:bg-[#F7D9A4]/40',
                        text: 'text-[#92400e]',
                        textSecondary: 'text-[#b45309]',
                        shadow: 'shadow-[0_8px_32px_rgba(247,217,164,0.25)]',
                        progressColor: '#F7D9A4',
                        buttonBg: 'bg-[#b45309]',
                        buttonText: 'text-blanc',
                        buttonHover: 'hover:bg-[#92400e]',
                        closeHover: 'hover:bg-[#92400e]/20',
                    };
                case 'info':
                    return {
                        background: 'bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE]',
                        backgroundOverlay: 'bg-gris-20/50',
                        borderColor: '#9DC0EE',
                        iconBg: 'bg-[#9DC0EE]/15',
                        iconBgHover: 'hover:bg-[#9DC0EE]/40',
                        text: 'text-[#1e40af]',
                        textSecondary: 'text-[#1d4ed8]',
                        shadow: 'shadow-[0_8px_32px_rgba(157,192,238,0.25)]',
                        progressColor: '#9DC0EE',
                        buttonBg: 'bg-[#1d4ed8]',
                        buttonText: 'text-blanc',
                        buttonHover: 'hover:bg-[#1e40af]',
                        closeHover: 'hover:bg-[#1e40af]/20',
                    };
                default:
                    return {
                        background: 'bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE]',
                        backgroundOverlay: 'bg-gris-20/50',
                        borderColor: '#9DC0EE',
                        iconBg: 'bg-[#9DC0EE]/15',
                        iconBgHover: 'hover:bg-[#9DC0EE]/40',
                        text: 'text-[#1e40af]',
                        textSecondary: 'text-[#1d4ed8]',
                        shadow: 'shadow-[0_8px_32px_rgba(157,192,238,0.25)]',
                        progressColor: '#9DC0EE',
                        buttonBg: 'bg-[#1d4ed8]',
                        buttonText: 'text-blanc',
                        buttonHover: 'hover:bg-[#1e40af]',
                        closeHover: 'hover:bg-[#1e40af]/20',
                    };
            }
        }
    };

    const Icon = getIcon(type);
    const styles = getStyles(type);

    const animationClass = isExiting 
        ? (isMobile ? 'animate-toast-exit-mobile' : 'animate-toast-exit')
        : (isMobile ? 'animate-toast-enter-mobile' : 'animate-toast-enter');

    // Calculer l'effet de profondeur pour les toasts empilés
    const isTopToast = index === total - 1;
    // Sur mobile, les toasts sont en haut, donc on décale vers le bas
    // Sur desktop, les toasts sont en bas, donc on décale vers le haut
    const depthOffset = isMobile 
        ? (total - 1 - index) * 4  // Sur mobile, les anciens toasts sont plus bas
        : (total - 1 - index) * 4; // Sur desktop, les anciens toasts sont plus haut (translateY négatif)
    const scale = isTopToast ? 1 : 1 - (total - 1 - index) * 0.02; // Légère réduction de taille
    const opacity = isTopToast ? 1 : 0.95 - (total - 1 - index) * 0.05; // Légère réduction d'opacité

    return (
        <div 
            ref={toastRef}
            className={`
                ${isMobile ? 'w-full rounded-b-2xl' : 'w-auto max-w-[520px] rounded-2xl'} 
                ${styles.background}
                border-l-4
                ${styles.shadow}
                backdrop-blur-sm
                ${animationClass}
                transition-all duration-300 ease-out
                overflow-hidden
                relative
            `}
            style={{ 
                borderLeftColor: styles.borderColor,
                touchAction: isMobile ? 'pan-y' : 'none',
                transform: isMobile 
                    ? `translateY(${depthOffset}px) scale(${scale})`
                    : `translateY(-${depthOffset}px) scale(${scale})`,
                opacity: opacity,
                zIndex: 10000 + index, // Les toasts plus récents ont un z-index plus élevé
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {/* Overlay gris pour le blur en mode light */}
            {!isDarkMode && styles.backgroundOverlay && (
                <div className={`absolute inset-0 ${styles.backgroundOverlay} backdrop-blur-sm ${isMobile ? 'rounded-b-2xl' : 'rounded-2xl'} pointer-events-none`} />
            )}
            <div className={`
                ${isMobile ? 'p-4 gap-3' : 'p-5 gap-4'} 
                flex items-start
                relative z-10
            `}>
                {/* Icône avec fond */}
                <div className={`
                    ${styles.iconBg}
                    rounded-xl p-2.5
                    flex-shrink-0
                    flex items-center justify-center
                `}>
                    <Icon className={`w-6 h-6 ${isMobile ? 'w-5 h-5' : 'w-6 h-6'}`} />
                </div>

                {/* Contenu */}
                <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                    {title && (
                        <p className={`
                            poppins-semibold 
                            ${isMobile ? 'text-sm' : 'text-base'} 
                            ${styles.text}
                        `}>
                            {title}
                        </p>
                    )}
                    <p className={`
                        poppins-regular 
                        ${isMobile ? 'text-xs' : 'text-sm'} 
                        ${styles.textSecondary}
                        leading-relaxed
                    `}>
                        {message}
                    </p>
                </div>

                {/* Actions */}
                <div className="flex items-start gap-2 flex-shrink-0">
                    {action && (
                        <button
                            onClick={action}
                            className={`
                                px-3 py-1.5
                                ${isMobile ? 'text-xs' : 'text-sm'}
                                poppins-medium
                                rounded-lg
                                ${styles.buttonBg || styles.iconBg}
                                ${styles.buttonText || styles.text}
                                ${styles.buttonHover || 'hover:opacity-80'}
                                active:scale-95
                                transition-all duration-200
                                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gris/50
                                font-semibold
                            `}
                        >
                            {actionText || 'Action'}
                        </button>
                    )}
                    {!isMobile && (
                        <button 
                            onClick={handleClose} 
                            className={`
                                p-1.5
                                rounded-lg
                                ${styles.textSecondary}
                                ${isDarkMode ? styles.iconBgHover : (styles.closeHover || 'hover:bg-gris-20')}
                                active:scale-95
                                transition-all duration-200
                                focus:outline-none
                                flex-shrink-0
                            `}
                            aria-label="Fermer"
                        >
                            <CrossIcon className={`w-5 h-5 ${isDarkMode ? 'fill-blanc' : ''}`} />
                        </button>
                    )}
                </div>
            </div>

            {/* Indicateur de swipe sur mobile */}
            {isMobile && (
                <div className="flex justify-center pb-3">
                    <div 
                        className="w-12 h-1 rounded-full opacity-40"
                        style={{ backgroundColor: styles.borderColor }}
                    />
                </div>
            )}
        </div>
    );
}

export default Toast;
